import React, { useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import Webcam from 'react-webcam'
import jsQR from 'jsqr'

// Decode attempts are throttled to this cadence. requestAnimationFrame still
// runs every paint, but we only actually run jsQR when this much time has
// passed — fast enough to feel instant, cheap enough to leave the main
// thread free between attempts.
const SCAN_INTERVAL_MS = 120

// Frames are downscaled so the longest edge is at most this many pixels
// before jsQR ever sees them. Decode cost scales with pixel count, and a QR
// code only needs a few hundred px across to be read reliably.
const SCAN_MAX_DIMENSION = 640

const QRScannerModal = ({ onCancel, onScan }) => {
    const webcamRef = useRef(null)
    const canvasRef = useRef(null)
    const contextRef = useRef(null)
    const rafRef = useRef(null)
    const lastScanAtRef = useRef(0)
    const lastVideoDimsRef = useRef({ width: 0, height: 0 })
    const doneRef = useRef(false)
    const stopCameraRef = useRef(() => {})

    useEffect(() => {
        doneRef.current = false
        stopCameraRef.current = () => {
            const stream = webcamRef.current?.video?.srcObject
            stream?.getTracks?.().forEach((track) => track.stop())
        }
        const stopCamera = stopCameraRef.current

        // Only re-allocate the canvas backing store when the source video's
        // actual resolution changes (e.g. camera renegotiation, device
        // rotation) — resizing a canvas every frame is one of the more
        // expensive canvas operations and the resolution is normally stable.
        const resizeCanvasForVideo = (video) => {
            const scale = Math.min(1, SCAN_MAX_DIMENSION / video.videoWidth, SCAN_MAX_DIMENSION / video.videoHeight)
            const canvas = canvasRef.current
            canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
            canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
            contextRef.current = canvas.getContext('2d', { willReadFrequently: true })
            lastVideoDimsRef.current = { width: video.videoWidth, height: video.videoHeight }
        }

        const tick = (timestamp) => {
            if (doneRef.current) return

            const video = webcamRef.current?.video

            if (video && video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth) {
                const dims = lastVideoDimsRef.current
                if (video.videoWidth !== dims.width || video.videoHeight !== dims.height) {
                    resizeCanvasForVideo(video)
                }

                if (timestamp - lastScanAtRef.current >= SCAN_INTERVAL_MS) {
                    lastScanAtRef.current = timestamp

                    const canvas = canvasRef.current
                    const context = contextRef.current
                    context.drawImage(video, 0, 0, canvas.width, canvas.height)
                    const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
                    const code = jsQR(imageData.data, imageData.width, imageData.height, {
                        inversionAttempts: 'attemptBoth'
                    })

                    if (code?.data) {
                        doneRef.current = true
                        stopCamera()
                        onScan(code.data)
                        return
                    }
                }
            }

            rafRef.current = requestAnimationFrame(tick)
        }

        rafRef.current = requestAnimationFrame(tick)

        // Note: intentionally NOT calling stopCamera() here. In development,
        // React.StrictMode mounts, cleans up, and re-mounts every effect once
        // to surface bugs like this — MediaStreamTrack.stop() is irreversible,
        // so doing it here would permanently kill the camera on first mount
        // while the (still-rendered) video element goes black. react-webcam
        // already stops its own stream when it genuinely unmounts.
        return () => {
            doneRef.current = true
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [onScan])

    const handleCancel = () => {
        doneRef.current = true
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        stopCameraRef.current()
        onCancel()
    }

    // Ensure modal is rendered in the DOM
    if (typeof document === 'undefined') {
        return null
    }

    return ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4">
            <div className="card-surface relative w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6 flex flex-col items-center">
                <h2 className="text-lg sm:text-xl font-semibold mb-1">Scan QR Code</h2>
                <p className="text-sm text-gray-600 mb-4 text-center">Align the QR code within the frame to scan.</p>

                <button onClick={handleCancel} className="btn-secondary mb-4 w-full sm:w-auto sm:self-end">
                    Cancel
                </button>

                <div className="relative w-full aspect-[4/3] sm:aspect-[10/9] bg-black rounded-lg overflow-hidden">
                    <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{
                            facingMode: 'environment',
                            width: { ideal: 1280 },
                            height: { ideal: 720 }
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />

                    {/* QR guide — corner-bracket viewfinder, scales with the frame */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-6 sm:p-10">
                        <div className="relative w-full h-full max-w-[280px] max-h-[280px]">
                            <span className="absolute top-0 left-0 w-8 h-8 sm:w-10 sm:h-10 border-t-4 border-l-4 border-red-500 rounded-tl-lg" />
                            <span className="absolute top-0 right-0 w-8 h-8 sm:w-10 sm:h-10 border-t-4 border-r-4 border-red-500 rounded-tr-lg" />
                            <span className="absolute bottom-0 left-0 w-8 h-8 sm:w-10 sm:h-10 border-b-4 border-l-4 border-red-500 rounded-bl-lg" />
                            <span className="absolute bottom-0 right-0 w-8 h-8 sm:w-10 sm:h-10 border-b-4 border-r-4 border-red-500 rounded-br-lg" />
                        </div>
                    </div>

                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
            </div>
        </div>,
        document.body
    )
}

export default QRScannerModal
